(() => {
  if (window.BZGalleryV4Loaded) return;
  window.BZGalleryV4Loaded = true;

  const bestSrc = (img) => {
    const srcset = img?.getAttribute('srcset') || '';
    if (srcset) {
      const candidates = srcset.split(',').map(s => s.trim()).filter(Boolean);
      const last = candidates[candidates.length - 1];
      if (last) return last.split(/\s+/)[0];
    }
    return img?.currentSrc || img?.src || '';
  };

  const normalizeBase = (url = '') => {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const filename = decodeURIComponent(pathname.split('/').pop() || '');
      return filename.replace(/\.[a-z0-9]+$/i, '').replace(/_\d+$/i, '').toLowerCase();
    } catch {
      return String(url).split('?')[0].split('/').pop().replace(/\.[a-z0-9]+$/i, '').replace(/_\d+$/i, '').toLowerCase();
    }
  };

  class BZGalleryV4 {
    constructor(gallery) {
      this.gallery = gallery;
      this.catalog = this.readCatalog();
      this.pool = [];
      this.index = 0;
      this.touchStartX = null;
      this.variantSwapToken = 0;
      this.pendingVariantMediaId = null;
      if (!this.catalog.length) return;
      this.preload();
      this.buildStage();
      this.bind();
      this.apply(this.getSelectedVariantMediaId(), 'instant');
      this.gallery.classList.add('bz-gallery-custom-ready');
    }

    readCatalog() {
      return Array.from(this.gallery.querySelectorAll('.product__media-list > li[data-media-id]'))
        .map((item) => {
          const img = item.querySelector('img');
          const raw = String(item.dataset.mediaId || '');
          const parts = raw.split('-');
          const mediaId = parts[parts.length - 1] || '';
          const src = bestSrc(img);
          return src ? { mediaId, src, alt: img?.alt || '', base: normalizeBase(src), item } : null;
        })
        .filter(Boolean);
    }

    preload() {
      this.catalog.forEach((entry) => {
        const image = new Image();
        image.src = entry.src;
      });
    }

    getSelectedVariantMediaId() {
      const script = this.gallery.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
      if (!script?.textContent) return null;
      try {
        const variant = JSON.parse(script.textContent);
        return variant?.featured_media?.id ? String(variant.featured_media.id) : null;
      } catch {
        return null;
      }
    }

    buildPool(mediaId) {
      const selected = this.catalog.find(x => x.mediaId === String(mediaId))
        || this.catalog[0];
      if (!selected) return [];

      const related = this.catalog.filter(x => x.base === selected.base);
      const rest = this.catalog.filter(x => x.base !== selected.base);
      return [selected, ...related.filter(x => x !== selected), ...rest];
    }

    buildStage() {
      const stage = document.createElement('div');
      stage.className = 'bz-gallery-custom';
      stage.innerHTML = `
        <div class="bz-gallery-hero">
          <button type="button" class="bz-gallery-arrow bz-gallery-arrow--prev" aria-label="Imagen anterior">‹</button>
          <button type="button" class="bz-gallery-hero-button" aria-label="Ver imagen">
            <img class="bz-gallery-hero-image" alt="">
          </button>
          <button type="button" class="bz-gallery-arrow bz-gallery-arrow--next" aria-label="Imagen siguiente">›</button>
        </div>
        <div class="bz-gallery-secondary-row">
          <button type="button" class="bz-gallery-secondary-button" data-slot="1"><img alt=""></button>
          <button type="button" class="bz-gallery-secondary-button" data-slot="2"><img alt=""></button>
        </div>
      `;
      this.gallery.prepend(stage);
      this.stage = stage;
      this.hero = stage.querySelector('.bz-gallery-hero-image');
      this.heroButton = stage.querySelector('.bz-gallery-hero-button');
      this.secondaryButtons = Array.from(stage.querySelectorAll('.bz-gallery-secondary-button'));
    }

    setImage(img, entry) {
      if (!img || !entry) return;
      if (img.dataset.mediaId !== entry.mediaId) {
        img.src = entry.src;
        img.alt = entry.alt || '';
        img.dataset.mediaId = entry.mediaId;
      }
    }

    animateHeroSwap(entry, direction) {
      if (!this.hero || !entry) return;
      const ghost = this.hero.cloneNode(false);
      ghost.classList.add('bz-gallery-hero-image--ghost');
      ghost.removeAttribute('data-media-id');
      this.heroButton.appendChild(ghost);

      this.setImage(this.hero, entry);
      this.hero.classList.remove(
        'bz-gallery-hero-image--enter-next',
        'bz-gallery-hero-image--enter-prev'
      );
      void this.hero.offsetWidth;

      if (direction > 0) {
        this.hero.classList.add('bz-gallery-hero-image--enter-next');
        ghost.classList.add('bz-gallery-hero-image--leave-next');
      } else {
        this.hero.classList.add('bz-gallery-hero-image--enter-prev');
        ghost.classList.add('bz-gallery-hero-image--leave-prev');
      }

      window.setTimeout(() => {
        ghost.remove();
        this.hero.classList.remove(
          'bz-gallery-hero-image--enter-next',
          'bz-gallery-hero-image--enter-prev'
        );
      }, 280);
    }


    async animateVariantSwap(entry) {
      if (!this.hero || !entry) return;
      if (this.hero.dataset.mediaId === entry.mediaId) return;

      const token = ++this.variantSwapToken;
      this.pendingVariantMediaId = entry.mediaId;

      const ghost = this.hero.cloneNode(false);
      ghost.classList.add('bz-gallery-hero-image--ghost', 'bz-gallery-variant-ghost');
      ghost.removeAttribute('data-media-id');
      this.heroButton.appendChild(ghost);

      const preload = new Image();
      preload.src = entry.src;

      try {
        if (preload.decode) await preload.decode();
        else if (!preload.complete) {
          await new Promise((resolve) => {
            preload.addEventListener('load', resolve, { once: true });
            preload.addEventListener('error', resolve, { once: true });
          });
        }
      } catch {}

      if (token !== this.variantSwapToken) {
        ghost.remove();
        return;
      }

      this.setImage(this.hero, entry);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      ghost.classList.add('is-leaving');
      window.setTimeout(() => {
        ghost.remove();
        if (token === this.variantSwapToken) this.pendingVariantMediaId = null;
      }, 160);
    }

    render({ mode = 'instant', direction = 1 } = {}) {
      if (!this.pool.length) return;
      const n = this.pool.length;
      this.index = ((this.index % n) + n) % n;

      const hero = this.pool[this.index];
      const second = n > 1 ? this.pool[(this.index + 1) % n] : null;
      const third = n > 2 ? this.pool[(this.index + 2) % n] : null;

      this.stage.classList.remove('bz-gallery-move-next', 'bz-gallery-move-prev');

      if (mode === 'slide') this.animateHeroSwap(hero, direction);
      else if (mode === 'variant') this.animateVariantSwap(hero);
      else this.setImage(this.hero, hero);

      this.secondaryButtons.forEach((button, i) => {
        const entry = i === 0 ? second : third;
        button.hidden = !entry;
        if (entry) {
          button.dataset.targetIndex = String((this.index + i + 1) % n);
          this.setImage(button.querySelector('img'), entry);
        }
      });

      this.stage.classList.toggle('bz-gallery-only-one', n === 1);
      this.stage.classList.toggle('bz-gallery-only-two', n === 2);

      if (mode === 'slide') {
        void this.stage.offsetWidth;
        this.stage.classList.add(direction > 0 ? 'bz-gallery-move-next' : 'bz-gallery-move-prev');
        window.setTimeout(() => {
          this.stage.classList.remove('bz-gallery-move-next', 'bz-gallery-move-prev');
        }, 280);
      }
    }

    apply(mediaId, mode = 'instant') {
      this.pool = this.buildPool(mediaId || this.getSelectedVariantMediaId());
      this.index = 0;
      this.render({ mode });
    }

    rotate(step) {
      if (this.pool.length < 2) return;
      this.index += step;
      this.render({ mode: 'slide', direction: step });
    }

    openCurrentNativeMedia() {
      const entry = this.pool[this.index];
      const opener = entry?.item?.querySelector('.product__modal-opener');
      opener?.click();
    }

    bind() {
      this.stage.querySelector('.bz-gallery-arrow--prev')?.addEventListener('click', () => this.rotate(-1));
      this.stage.querySelector('.bz-gallery-arrow--next')?.addEventListener('click', () => this.rotate(1));
      this.heroButton?.addEventListener('click', () => this.openCurrentNativeMedia());

      this.secondaryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const target = Number(button.dataset.targetIndex);
          if (!Number.isFinite(target)) return;
          const direction = target >= this.index ? 1 : -1;
          this.index = target;
          this.render({ mode: 'slide', direction });
        });
      });

      this.heroButton?.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches?.[0]?.clientX ?? null;
      }, { passive: true });

      this.heroButton?.addEventListener('touchend', (event) => {
        if (this.touchStartX == null) return;
        const endX = event.changedTouches?.[0]?.clientX;
        if (typeof endX !== 'number') return;
        const delta = endX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(delta) >= 45) this.rotate(delta < 0 ? 1 : -1);
      }, { passive: true });


    }
  }

  const init = () => {
    document.querySelectorAll('media-gallery[data-bz-gallery-pilot="true"]').forEach((gallery) => {
      if (!gallery._bzGalleryV4) gallery._bzGalleryV4 = new BZGalleryV4(gallery);
    });
  };

  const onVariant = ({ data } = {}) => {
    const sectionId = data?.sectionId;
    const mediaId = data?.variant?.featured_media?.id;
    requestAnimationFrame(() => {
      document.querySelectorAll('media-gallery[data-bz-gallery-pilot="true"]').forEach((gallery) => {
        if (sectionId && !gallery.id.endsWith(sectionId)) return;
        const instance = gallery._bzGalleryV4;
        if (!instance || !mediaId) return;
        const id = String(mediaId);
        if (id === instance.pendingVariantMediaId || id === instance.hero?.dataset.mediaId) return;
        instance.apply(id, 'variant');
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariant);
  }

  document.addEventListener('shopify:section:load', init);
})();